/**
 * dataRetentionStore.js
 * ──────────────────────
 * GDPR Art. 5(1)(e) storage limitation + Art. 17 right-to-erasure handling.
 *
 * Two interrelated mechanisms:
 *
 *   1. Retention policy (per tenant)
 *      Defines how long different categories of personal data are kept after
 *      an employee exits the organisation. When the retention period elapses,
 *      the auto-purge scheduler removes the data (or anonymises it for fields
 *      legitimately retained for statistical pay-equity reporting).
 *
 *   2. Erasure requests (per data subject)
 *      Active right-to-erasure requests under GDPR Art. 17. An employee
 *      submits a request → admin/approver reviews → executed immediately or
 *      refused with documented reason (e.g. legal hold, tax retention).
 *
 * What's actually stored
 * ──────────────────────
 * In this UAT, "data" lives in localStorage namespaces, request store, task
 * store, AI audit log, parsed CV profile. The purge worker iterates these
 * known stores and removes records matching the data subject. In production
 * the same logic talks to Postgres, S3, and email queues.
 *
 * Anonymisation vs deletion
 * ──────────────────────────
 * Art. 17 doesn't apply to data that's been irreversibly anonymised (not
 * "personal data" anymore). For pay-equity statistics, we retain aggregated
 * gender/salary cells with no individual identifiers, that legitimately
 * stays past the retention window because it's no longer covered by GDPR.
 * The UI flags which fields are anonymised vs deleted so admins can explain
 * to data subjects what survives erasure and why.
 */

import { isOnHold, getActiveHold } from './legalHoldStore';
import { pseudonymiseSync, applyKAnonymity } from './anonymizationService';

const POLICY_KEY = (tenantId) => `clearequal.retentionPolicy.${tenantId}`;
const REQUEST_KEY = 'clearequal.erasureRequests.v1';
const AUDIT_KEY = 'clearequal.purgeAudit.v1';
const EVENT = 'clearequal.retention.changed';

// ─── Default retention policy ────────────────────────────────────────────
// Days kept after employee exit date, per data category.
// Defaults reflect typical EU HR practice; admin can override per tenant.
export const DEFAULT_POLICY = {
  // Active personal data, name, email, contact, address, profile photo
  contact_data:        { retentionDays: 180, action: 'delete', legalBasis: 'GDPR Art. 17, no remaining lawful basis after exit' },

  // Compensation history, base salary, bonus, equity grants
  compensation_data:   { retentionDays: 1825, action: 'delete', legalBasis: 'Tax law retention (5 years EU avg)' },

  // CV / skills profile, uploaded by employee for self-service skills
  cv_profile:          { retentionDays: 90,  action: 'delete', legalBasis: 'Voluntary submission, minimal retention after exit' },

  // Information requests submitted by the employee under Art. 7
  info_requests:       { retentionDays: 365, action: 'delete', legalBasis: 'GDPR Art. 30 records of processing, 1 year' },

  // Pay equity statistics, gender/level cells used for aggregate reporting
  // ANONYMISED rather than deleted because aggregated data with no individual
  // identifier is not personal data under GDPR Art. 4(1).
  pay_equity_stats:    { retentionDays: 0, action: 'anonymise', legalBasis: 'Anonymised aggregates fall outside GDPR scope' },

  // AI audit log, records of automated decisions (skill matching, role suggestions)
  ai_audit_log:        { retentionDays: 1095, action: 'delete', legalBasis: 'EU AI Act Art. 12 logs, 6 months minimum, 3 years recommended' },

  // System audit trail, required for SOC 2 / compliance
  audit_trail:         { retentionDays: 2555, action: 'delete', legalBasis: 'SOC 2 / accountability, 7 years' },
};

// ─── Policy CRUD ─────────────────────────────────────────────────────────
export function getRetentionPolicy(tenantId) {
  if (!tenantId) return structuredClone(DEFAULT_POLICY);
  try {
    const raw = localStorage.getItem(POLICY_KEY(tenantId));
    if (!raw) return structuredClone(DEFAULT_POLICY);
    const stored = JSON.parse(raw);
    // Merge defaults so newly-added categories show up for existing tenants
    return { ...DEFAULT_POLICY, ...stored };
  } catch {
    return structuredClone(DEFAULT_POLICY);
  }
}

export function updateRetentionPolicy(tenantId, partialPolicy) {
  if (!tenantId) return;
  const current = getRetentionPolicy(tenantId);
  const next = { ...current, ...partialPolicy };
  try {
    localStorage.setItem(POLICY_KEY(tenantId), JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(EVENT));
  } catch (_) {}
  return next;
}

// ─── Erasure request store ──────────────────────────────────────────────
export const ERASURE_STATUS = {
  PENDING: 'pending',           // submitted, awaiting admin review
  APPROVED: 'approved',         // admin approved, awaiting execution
  REJECTED: 'rejected',         // refused (legal hold, etc.)
  EXECUTED: 'executed',         // purge complete
  CANCELLED: 'cancelled',       // withdrawn by data subject before execution
};

function readRequests() {
  try {
    const raw = localStorage.getItem(REQUEST_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeRequests(items) {
  try {
    localStorage.setItem(REQUEST_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent(EVENT));
  } catch (_) {}
}

/**
 * Create a new erasure request.
 *   subjectEmail , the data subject (employee whose data should be erased)
 *   submittedBy  , who clicked the button (subject themselves, or admin acting for them)
 *   reason       , free-text justification
 *   scope        , array of data category keys, or ['all']
 */
export function submitErasureRequest({
  subjectEmail,
  subjectName,
  submittedBy,
  reason = '',
  scope = ['all'],
  tenantId,
}) {
  const all = readRequests();
  const req = {
    id: `er-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    tenantId,
    subjectEmail,
    subjectName,
    submittedBy,
    submittedAt: new Date().toISOString(),
    reason,
    scope,
    status: ERASURE_STATUS.PENDING,
    // GDPR Art. 12(3), must respond within 1 month
    deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  };
  all.push(req);
  writeRequests(all);
  return req;
}

export function listErasureRequests(filter = {}) {
  let all = readRequests();
  if (filter.tenantId) all = all.filter((r) => r.tenantId === filter.tenantId);
  if (filter.status) all = all.filter((r) => r.status === filter.status);
  if (filter.subjectEmail) all = all.filter((r) => r.subjectEmail === filter.subjectEmail);
  return all;
}

export function getErasureRequest(id) {
  return readRequests().find((r) => r.id === id) || null;
}

/**
 * Approve a request (admin action). Does NOT yet execute the purge,
 * execution is a separate step so admin and approver can be different people
 * (two-person rule for destructive actions).
 */
export function approveErasure(id, approvedBy, approverNote = '') {
  const all = readRequests();
  const idx = all.findIndex((r) => r.id === id);
  if (idx === -1) throw new Error('Erasure request not found');

  const req = all[idx];
  if (req.status !== ERASURE_STATUS.PENDING) {
    throw new Error(`Cannot approve, current status is ${req.status}`);
  }
  if (req.submittedBy === approvedBy) {
    throw new Error('Self-approval is not permitted for erasure requests');
  }

  all[idx] = {
    ...req,
    status: ERASURE_STATUS.APPROVED,
    approvedBy,
    approvedAt: new Date().toISOString(),
    approverNote,
  };
  writeRequests(all);
  return all[idx];
}

export function rejectErasure(id, rejectedBy, rejectionReason) {
  if (!rejectionReason || !rejectionReason.trim()) {
    throw new Error('A rejection reason is required (GDPR Art. 12(4) transparency)');
  }
  const all = readRequests();
  const idx = all.findIndex((r) => r.id === id);
  if (idx === -1) throw new Error('Erasure request not found');

  all[idx] = {
    ...all[idx],
    status: ERASURE_STATUS.REJECTED,
    rejectedBy,
    rejectedAt: new Date().toISOString(),
    rejectionReason,
  };
  writeRequests(all);
  return all[idx];
}

export function cancelErasure(id, cancelledBy) {
  const all = readRequests();
  const idx = all.findIndex((r) => r.id === id);
  if (idx === -1) throw new Error('Erasure request not found');
  if (all[idx].status !== ERASURE_STATUS.PENDING) {
    throw new Error('Only pending requests can be cancelled');
  }
  all[idx] = {
    ...all[idx],
    status: ERASURE_STATUS.CANCELLED,
    cancelledBy,
    cancelledAt: new Date().toISOString(),
  };
  writeRequests(all);
  return all[idx];
}

/**
 * Execute an approved request. Iterates the known data stores and removes /
 * anonymises records belonging to the data subject. Writes an immutable
 * audit entry summarising what was deleted.
 *
 * Refuses execution and auto-rejects the request if the subject has an
 * active legal hold (GDPR Art. 17(3) exemption).
 */
export function executeErasure(id, executedBy) {
  const all = readRequests();
  const idx = all.findIndex((r) => r.id === id);
  if (idx === -1) throw new Error('Erasure request not found');

  const req = all[idx];
  if (req.status !== ERASURE_STATUS.APPROVED) {
    throw new Error(`Cannot execute, current status is ${req.status}`);
  }

  // Refuse if subject is on legal hold (Art. 17(3) exemption)
  if (isOnHold(req.subjectEmail, req.tenantId)) {
    const hold = getActiveHold(req.subjectEmail, req.tenantId);
    // Auto-reject the erasure rather than execute it
    all[idx] = {
      ...req,
      status: ERASURE_STATUS.REJECTED,
      rejectedBy: 'system-legal-hold-guard',
      rejectedAt: new Date().toISOString(),
      rejectionReason: `Subject is on active legal hold (${hold.legalBasis}, placed ${hold.placedAt}, reason: "${hold.reason}"). ` +
        `GDPR Art. 17(3)(b)/(e) exempts this from the right to erasure until the hold is released.`,
    };
    writeRequests(all);
    throw new Error('Cannot execute erasure, subject is on legal hold (Art. 17(3) exemption applied)');
  }

  const summary = purgeDataSubject(req.subjectEmail, req.scope, req.tenantId, {
    actor: executedBy,
    requestId: req.id,
    reason: 'GDPR Art. 17 erasure request',
  });

  all[idx] = {
    ...req,
    status: ERASURE_STATUS.EXECUTED,
    executedBy,
    executedAt: new Date().toISOString(),
    purgeSummary: summary,
  };
  writeRequests(all);
  return all[idx];
}

// ─── Purge engine ────────────────────────────────────────────────────────
/**
 * Iterate the known stores and purge records matching the subject email.
 * Each store has its own logic, request store, task store, AI audit log,
 * CV profile, etc. Returns a summary used for the audit log.
 *
 * The purge is BEST-EFFORT in this UAT, failures are logged but do not
 * abort the whole operation. The audit summary records exactly what was
 * touched so the data subject can be told what survived (e.g. anonymised
 * statistics) and why.
 */
export function purgeDataSubject(subjectEmail, scope = ['all'], tenantId, meta = {}) {
  const summary = {
    subjectEmail,
    tenantId,
    timestamp: new Date().toISOString(),
    actor: meta.actor || 'system',
    requestId: meta.requestId || null,
    reason: meta.reason || 'Auto-purge per retention policy',
    scope,
    stores: [],
  };

  const includes = (cat) => scope.includes('all') || scope.includes(cat);

  // 1. Information requests
  if (includes('info_requests')) {
    const removed = purgeFromStore('clearequal.requests.v1', (r) => r.employeeEmail === subjectEmail || r.email === subjectEmail);
    summary.stores.push({ store: 'info_requests', removed });
  }

  // 2. CV profile
  if (includes('cv_profile')) {
    try {
      const cvKey = `clearequal.cvProfile.${subjectEmail}`;
      const had = !!localStorage.getItem(cvKey);
      localStorage.removeItem(cvKey);
      summary.stores.push({ store: 'cv_profile', removed: had ? 1 : 0 });
    } catch (_) {
      summary.stores.push({ store: 'cv_profile', removed: 0, error: 'access_denied' });
    }
  }

  // 3. Tasks where the subject is the assignee, reassign rather than delete
  if (includes('contact_data') || includes('all')) {
    try {
      const raw = localStorage.getItem('clearequal.tasks.v1');
      if (raw) {
        const tasks = JSON.parse(raw);
        let touched = 0;
        const cleaned = tasks.map((t) => {
          if (t.assignee === subjectEmail) {
            touched++;
            return { ...t, assignee: null, autoAssigned: false, reassignedDueToErasure: true };
          }
          return t;
        });
        if (touched > 0) {
          localStorage.setItem('clearequal.tasks.v1', JSON.stringify(cleaned));
        }
        summary.stores.push({ store: 'tasks_reassigned', removed: touched });
      }
    } catch (_) { /* */ }
  }

  // 4. AI audit log entries about this subject
  if (includes('ai_audit_log')) {
    const removed = purgeFromStore('clearequal.aiAuditLog.v1', (e) => e.subjectEmail === subjectEmail || e.userEmail === subjectEmail);
    summary.stores.push({ store: 'ai_audit_log', removed });
  }

  // 5. Notification preferences / settings blob
  if (includes('contact_data')) {
    try {
      const raw = localStorage.getItem('clearequal.notifPrefs');
      if (raw) {
        // We don't store an email in notif prefs in this UAT, but production
        // would scope per-user. Note for completeness.
        summary.stores.push({ store: 'notification_prefs', removed: 0, note: 'no per-user record in UAT' });
      }
    } catch (_) { /* */ }
  }

  // 6. Pay equity stats, anonymise via k-anonymity, do not delete.
  // The subject's individual record gets stripped of direct identifiers and
  // pseudonymised so the aggregate stats remain useful for compliance
  // reporting but no longer count as personal data under GDPR Art. 4(1).
  if (includes('pay_equity_stats')) {
    try {
      const statsKey = `clearequal.payEquityStats.${tenantId}`;
      const raw = localStorage.getItem(statsKey);
      if (raw) {
        const records = JSON.parse(raw);
        if (Array.isArray(records) && records.length > 0) {
          // Pseudonymise the subject's records using sync hash (no async in
          // this synchronous path). Production should use async crypto.subtle.
          const updated = records.map((r) => {
            if (r.email === subjectEmail || r.employeeEmail === subjectEmail) {
              return {
                ...r,
                email: undefined,
                employeeEmail: undefined,
                employeeName: undefined,
                pseudonym: pseudonymiseSync(subjectEmail, tenantId),
                anonymisedAt: new Date().toISOString(),
              };
            }
            return r;
          });
          localStorage.setItem(statsKey, JSON.stringify(updated));

          // Apply k-anonymity: re-validate that all remaining cells still
          // meet the k-threshold after anonymisation. If a cell drops below
          // k due to suppression, log it for the DPO.
          const kCheck = applyKAnonymity(
            updated,
            ['gender', 'department', 'level'],
            5
          );
          summary.stores.push({
            store: 'pay_equity_stats',
            removed: 0,
            anonymised: true,
            pseudonymised: true,
            kAnonymityCheck: {
              k: 5,
              suppressedGroups: kCheck.summary.suppressedGroupCount,
              releasedGroups: kCheck.summary.releasedGroupCount,
            },
            note: 'Direct identifiers replaced with tenant-scoped hash. K=5 anonymity verified on remaining release set.',
          });
        } else {
          summary.stores.push({
            store: 'pay_equity_stats',
            removed: 0,
            anonymised: true,
            note: 'No stat records present in UAT store; production would anonymise the warehouse fact table.',
          });
        }
      } else {
        summary.stores.push({
          store: 'pay_equity_stats',
          removed: 0,
          anonymised: true,
          note: 'No stat records present in UAT store; production would anonymise the warehouse fact table.',
        });
      }
    } catch (err) {
      summary.stores.push({
        store: 'pay_equity_stats',
        removed: 0,
        error: 'anonymisation_failed',
        message: String(err?.message || err),
      });
    }
  }

  writeAudit(summary);
  return summary;
}

function purgeFromStore(storageKey, predicate) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return 0;
    const items = JSON.parse(raw);
    if (!Array.isArray(items)) return 0;
    const before = items.length;
    const remaining = items.filter((i) => !predicate(i));
    localStorage.setItem(storageKey, JSON.stringify(remaining));
    return before - remaining.length;
  } catch {
    return 0;
  }
}

// ─── Purge audit log ────────────────────────────────────────────────────
function readAudit() {
  try {
    const raw = localStorage.getItem(AUDIT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeAudit(entry) {
  try {
    const all = readAudit();
    all.unshift(entry);
    // Cap at 500 entries to avoid storage bloat in UAT, production has unlimited DB
    if (all.length > 500) all.length = 500;
    localStorage.setItem(AUDIT_KEY, JSON.stringify(all));
  } catch (_) { /* */ }
}

export function listPurgeAudit(filter = {}) {
  let all = readAudit();
  if (filter.tenantId) all = all.filter((a) => a.tenantId === filter.tenantId);
  if (filter.subjectEmail) all = all.filter((a) => a.subjectEmail === filter.subjectEmail);
  return all;
}

// ─── Auto-purge scheduler ───────────────────────────────────────────────
/**
 * Run the auto-purge sweep. Scans for ex-employees whose retention windows
 * have elapsed and purges their data per the active policy.
 *
 * In this UAT we don't have a real list of "ex-employees with exit dates",
 * so the scheduler operates only against records that have an explicit
 * `exitDate` field (used by the demo seed data and any future records).
 *
 * Production note: this should run as a scheduled serverless function
 * (Vercel cron, AWS EventBridge, etc.), not in the browser. The browser
 * version here is a demonstration that runs on app load so testers can see
 * the mechanism work end-to-end.
 */
export function runAutoPurge(tenantId) {
  const policy = getRetentionPolicy(tenantId);
  const now = Date.now();
  const purged = [];

  // For each retention category, find records past the window and purge.
  // The actual scan happens only on stores with subject metadata.
  // This is a placeholder for the real cron-driven scan.
  const lastRunKey = `clearequal.lastAutoPurge.${tenantId}`;
  const lastRun = parseInt(localStorage.getItem(lastRunKey) || '0', 10);

  // Don't run more than once per hour to avoid hammering on every page load
  if (now - lastRun < 60 * 60 * 1000) {
    return { ranAt: new Date(lastRun).toISOString(), skipped: 'rate-limited' };
  }

  try {
    localStorage.setItem(lastRunKey, String(now));
  } catch (_) { /* */ }

  // Auto-execute approved erasures that have been waiting
  const approved = listErasureRequests({ tenantId, status: ERASURE_STATUS.APPROVED });
  for (const req of approved) {
    // Skip if subject is on legal hold, leave the request approved so DPO can decide
    if (isOnHold(req.subjectEmail, req.tenantId)) {
      continue;
    }
    try {
      executeErasure(req.id, 'system-autopurge');
      purged.push({ requestId: req.id, subjectEmail: req.subjectEmail });
    } catch (e) {
      // Continue on error, this is best-effort
    }
  }

  return {
    ranAt: new Date().toISOString(),
    autoExecuted: purged.length,
    policyApplied: Object.keys(policy).length,
  };
}

// ─── Subscriptions ──────────────────────────────────────────────────────
export function subscribe(callback) {
  const handler = () => callback();
  window.addEventListener(EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}
